<template>
  <div class="view-deal-page">
    <v-card :loading="loading">
      <v-card-title class="d-flex flex-wrap align-center">
        <div>
          <div class="d-flex align-center flex-wrap">
            <div class="text-h6 mr-3">{{ deal.name }}</div>
            <v-chip
              v-if="deal.id"
              small
              :color="deal.trade_side === 'selling' ? 'orange' : 'primary'"
              dark
            >
              {{ tradeSideLabel }}
            </v-chip>
          </div>
          <div v-if="deal.code" class="text-caption grey--text">{{ deal.code }}</div>
        </div>
        <v-spacer />
        <v-btn text small @click="$router.push({ name: 'deals-list' })">
          Back
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" class="primary" dark>
        <v-tab href="#overview">{{ $t("DEALS.VIEW.OVERVIEW") }}</v-tab>
        <v-tab href="#documents">{{ $t("DEALS.VIEW.DOCUMENTS") }}</v-tab>
        <v-tab href="#comments">{{ $t("DEALS.VIEW.COMMENTS") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="overview">
          <v-card flat>
            <v-card-text>
              <DealCustomerRequestsPanel
                :deal-id="dealId"
                :trade-side="deal.trade_side || 'buying'"
                :can-edit="hasPermissions('deals-edit')"
              />

              <v-divider class="my-4" />

              <v-row>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">{{ $t("DEALS.MODAL.ORGANIZATION") }}</div>
                  <div>{{ deal.organization && deal.organization.name }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">{{ $t("DEALS.MODAL.STATUS") }}</div>
                  <div>{{ deal.deal_status && deal.deal_status.name }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">{{ $t("DEALS.MODAL.SUPPLIERS") }}</div>
                  <div v-if="supplierNames.length" class="d-flex flex-wrap">
                    <v-chip
                      v-for="(name, index) in supplierNames"
                      :key="`supplier-${index}`"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ name }}
                    </v-chip>
                  </div>
                  <div v-else>—</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">{{ $t("DEALS.MODAL.CLIENTS") }}</div>
                  <div v-if="clientNames.length" class="d-flex flex-wrap">
                    <v-chip
                      v-for="(name, index) in clientNames"
                      :key="`client-${index}`"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ name }}
                    </v-chip>
                  </div>
                  <div v-else>—</div>
                </v-col>
                <v-col cols="12">
                  <div class="text-caption grey--text">{{ $t("DEALS.MODAL.NOTE") }}</div>
                  <div>{{ deal.note || "—" }}</div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <DealHealthBar :summary="workflowSummary" />

              <div class="text-subtitle-2 mb-3">{{ $t("DEALS.VIEW.QUICK_ACTIONS") }}</div>
              <div class="deal-quick-actions">
                <DealQuickActionTile
                  v-if="hasPermissions('stock-sales-orders-create')"
                  :label="$t('DEALS.VIEW.NEW_SO')"
                  icon="mdi-cart-outline"
                  variant="sales-order"
                  @click="createSalesOrder"
                />
                <DealQuickActionTile
                  v-if="hasPermissions('stock-purchase-orders-create')"
                  :label="$t('DEALS.VIEW.NEW_PO')"
                  icon="mdi-clipboard-text-outline"
                  variant="purchase-order"
                  @click="createPurchaseOrder"
                />
                <DealQuickActionTile
                  v-if="hasPermissions('stock-receipts-create')"
                  :label="$t('DEALS.VIEW.NEW_RECEIPT')"
                  icon="mdi-package-down"
                  variant="receipt"
                  @click="createReceipt"
                />
                <DealQuickActionTile
                  v-if="hasPermissions('stock-sales-create')"
                  :label="$t('DEALS.VIEW.NEW_DELIVERY')"
                  icon="mdi-truck-delivery"
                  variant="delivery"
                  @click="createDelivery"
                />
                <DealQuickActionTile
                  v-if="canCreateDealTransport"
                  :label="$t('STOCK.TRANSPORT_ORDERS.ORGANIZE_TRANSPORT')"
                  icon="mdi-airplane"
                  variant="transport"
                  @click="createTransportOrder"
                />
              </div>

              <v-divider class="my-4" />

              <div class="d-flex align-center mb-3">
                <div class="text-subtitle-2">{{ $t("DEALS.VIEW.WORKFLOW") }}</div>
                <v-spacer />
                <v-btn icon small :loading="workflowLoading" @click="loadWorkflow">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <div v-if="workflowLoading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary" size="24" />
              </div>
              <div v-else-if="!workflowChains.length && !standaloneItems.length" class="grey--text text-body-2 mb-4">
                {{ $t("DEALS.VIEW.NO_ACTIVITY") }}
              </div>
              <div v-else>
                <v-expansion-panels v-if="workflowChains.length" v-model="openChains" multiple flat class="mb-4">
                  <DealWorkflowChain
                    v-for="chain in workflowChains"
                    :key="`${chain.root.kind}-${chain.root.id}`"
                    :chain="chain"
                    :downloading-file-id="downloadingFileId"
                    @download-file="downloadNodeFile"
                  >
                    <template #node-actions="{ item }">
                      <DealWorkflowActions
                        :item="item"
                        @create-po-from-so="openCreatePoFromSo"
                        @create-delivery-from-so="createDeliveryFromSo"
                        @organize-transport-from-so="organizeTransportFromSo"
                        @create-receipt-from-po="createReceiptFromPo"
                        @organize-transport-from-po="organizeTransportFromPo"
                        @create-receipt-from-to="createReceiptFromTo"
                        @create-delivery-from-to="createDeliveryFromTo"
                      />
                    </template>
                  </DealWorkflowChain>
                </v-expansion-panels>

                <div v-if="standaloneItems.length">
                  <div class="text-subtitle-2 mb-2">{{ $t("DEALS.VIEW.STANDALONE") }}</div>
                  <div
                    v-for="item in standaloneItems"
                    :key="`standalone-${item.kind}-${item.id}`"
                    class="deal-workflow-standalone mb-2"
                  >
                    <DealWorkflowNode
                      :item="item"
                      :show-actions="true"
                      :show-progress="hasProgress(item)"
                      :downloading-file-id="downloadingFileId"
                      @download-file="downloadNodeFile(item, $event)"
                    >
                      <template #actions>
                        <DealWorkflowActions
                          :item="item"
                          @create-po-from-so="openCreatePoFromSo"
                          @create-delivery-from-so="createDeliveryFromSo"
                          @organize-transport-from-so="organizeTransportFromSo"
                          @create-receipt-from-po="createReceiptFromPo"
                          @organize-transport-from-po="organizeTransportFromPo"
                          @create-receipt-from-to="createReceiptFromTo"
                          @create-delivery-from-to="createDeliveryFromTo"
                        />
                      </template>
                    </DealWorkflowNode>
                  </div>
                </div>
              </div>

              <v-dialog v-model="createPoDialog" max-width="480">
                <v-card>
                  <v-card-title>{{ $t("DEALS.VIEW.CREATE_PO_FROM_SO") }}</v-card-title>
                  <v-card-text>
                    <v-autocomplete
                      v-model="createPoSupplierId"
                      :items="supplierOptions"
                      :search-input.sync="supplierSearch"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.PURCHASE_ORDERS.SUPPLIER') + '*'"
                      clearable
                    />
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="createPoDialog = false">
                      {{ $t("GENERAL.LABELS.CANCEL") }}
                    </v-btn>
                    <v-btn
                      color="primary"
                      :loading="createPoLoading"
                      :disabled="!createPoSupplierId"
                      @click="confirmCreatePoFromSo"
                    >
                      {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item value="documents">
          <DealDocumentsTab :deal-id="dealId" :active="tab === 'documents'" />
        </v-tab-item>

        <v-tab-item value="comments">
          <v-card flat>
            <v-card-text>
              <CommentThread
                :deal-id="dealId"
                :can-edit="hasPermissions('deals-edit')"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import CommentThread from "@/views/partials/comments/CommentThread.vue";
import DealCustomerRequestsPanel from "@/views/pages/deals/partials/DealCustomerRequestsPanel.vue";
import DealDocumentsTab from "@/views/pages/deals/partials/DealDocumentsTab.vue";
import DealHealthBar from "@/views/pages/deals/partials/DealHealthBar.vue";
import DealQuickActionTile from "@/views/pages/deals/partials/DealQuickActionTile.vue";
import DealWorkflowActions from "@/views/pages/deals/partials/DealWorkflowActions.vue";
import DealWorkflowChain from "@/views/pages/deals/partials/DealWorkflowChain.vue";
import DealWorkflowNode from "@/views/pages/deals/partials/DealWorkflowNode.vue";
import { GET_DEAL, GET_DEAL_WORKFLOW } from "@/store/deals.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { DOWNLOAD_STOCK_PO_FILE } from "@/store/stock/stockPurchaseOrders.module";
import { DOWNLOAD_STOCK_RECEIPT_FILE } from "@/store/stock/stockReceipts.module";
import { DOWNLOAD_STOCK_SO_FILE } from "@/store/stock/stockSalesOrders.module";
import { DOWNLOAD_STOCK_SALE_FILE } from "@/store/stock/stockSales.module";
import { DOWNLOAD_STOCK_TO_FILE } from "@/store/stock/stockTransportOrders.module";
import { DOWNLOAD_STOCK_INVOICE_FILE } from "@/store/stock/stockInvoices.module";
import { SHOW_SNACK } from "@/store/snack.module";

const DOWNLOAD_BY_KIND = {
  purchase_order: DOWNLOAD_STOCK_PO_FILE,
  sales_order: DOWNLOAD_STOCK_SO_FILE,
  receipt: DOWNLOAD_STOCK_RECEIPT_FILE,
  delivery: DOWNLOAD_STOCK_SALE_FILE,
  transport_order: DOWNLOAD_STOCK_TO_FILE,
  invoice: DOWNLOAD_STOCK_INVOICE_FILE,
};

export default {
  name: "ViewDeal",
  components: {
    CommentThread,
    DealCustomerRequestsPanel,
    DealDocumentsTab,
    DealHealthBar,
    DealQuickActionTile,
    DealWorkflowActions,
    DealWorkflowChain,
    DealWorkflowNode,
  },
  data() {
    return {
      tab: "overview",
      loading: false,
      workflowLoading: false,
      createPoLoading: false,
      deal: {},
      workflowChains: [],
      standaloneItems: [],
      workflowSummary: null,
      openChains: [],
      downloadingFileId: null,
      createPoDialog: false,
      createPoSupplierId: null,
      createPoFromSoId: null,
      supplierOptions: [],
      supplierSearch: "",
    };
  },
  computed: {
    dealId() {
      return Number(this.$route.params.id);
    },
    dealOrganizationId() {
      return (
        this.deal.organization_id ||
        (this.deal.organization && this.deal.organization.id) ||
        null
      );
    },
    canCreateDealTransport() {
      const slug = this.deal?.deal_status?.slug;
      if (["cancelled", "completed"].includes(slug)) {
        return false;
      }
      return this.hasPermissions("stock-transport-orders-create");
    },
    tradeSideLabel() {
      const side = this.deal.trade_side === "selling" ? "SELLING" : "BUYING";
      return this.$t(`DEALS.TRADE_SIDE.${side}`);
    },
    supplierNames() {
      return this.dealCompanyNames("suppliers");
    },
    clientNames() {
      return this.dealCompanyNames("clients");
    },
    dealSupplierOptions() {
      return this.dealCompanies("suppliers");
    },
  },
  watch: {
    "$route.params.id"() {
      this.loadDeal();
      this.loadWorkflow();
    },
    supplierSearch() {
      this.loadSuppliers();
    },
  },
  created() {
    this.loadDeal();
    this.loadWorkflow();
  },
  methods: {
    dealQuery(extra = {}) {
      const query = { ...extra };
      if (this.dealId) {
        query.deal_id = this.dealId;
      }
      if (this.dealOrganizationId) {
        query.organization_id = this.dealOrganizationId;
      }
      return query;
    },
    dealCompanies(key) {
      return (this.deal[key] || [])
        .filter((company) => company && company.id)
        .map((company) => ({
          ...company,
          id: Number(company.id),
          label: company.label || company.name || String(company.id),
        }));
    },
    dealCompanyNames(key) {
      return this.dealCompanies(key)
        .map((company) => company.label)
        .filter(Boolean);
    },
    singleDealCompanyId(key) {
      const companies = this.dealCompanies(key);
      return companies.length === 1 ? companies[0].id : null;
    },
    partyQuery(key, queryKey) {
      const id = this.singleDealCompanyId(key);
      return id ? { [queryKey]: id } : {};
    },
    hasProgress(item) {
      return item.progress && (item.kind === "sales_order" || item.kind === "purchase_order");
    },
    createPurchaseOrder() {
      this.$router.push({
        name: "stock-purchase-orders-create",
        query: this.dealQuery(this.partyQuery("suppliers", "supplier_company_id")),
      });
    },
    createSalesOrder() {
      this.$router.push({
        name: "stock-sales-orders-create",
        query: this.dealQuery(this.partyQuery("clients", "customer_company_id")),
      });
    },
    createReceipt() {
      this.$router.push({
        name: "stock-receipts-create",
        query: this.dealQuery(this.partyQuery("suppliers", "source_company_id")),
      });
    },
    createDelivery() {
      this.$router.push({
        name: "stock-sales-create",
        query: this.dealQuery(this.partyQuery("clients", "destination_company_id")),
      });
    },
    createTransportOrder() {
      this.$router.push({
        name: "stock-transport-orders-create",
        query: this.dealQuery({ direction: "inbound" }),
      });
    },
    async loadWorkflow() {
      this.workflowLoading = true;
      try {
        const data = await this.$store.dispatch(GET_DEAL_WORKFLOW, this.dealId);
        this.workflowChains = data.chains || [];
        this.standaloneItems = data.standalone || [];
        this.workflowSummary = data.summary || null;
        this.openChains = this.workflowChains.map((_, index) => index);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.workflowLoading = false;
      }
    },
    async loadSuppliers() {
      const dealSuppliers = this.dealSupplierOptions;
      if (!this.supplierSearch && dealSuppliers.length) {
        this.supplierOptions = dealSuppliers;
        return;
      }
      try {
        const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
          search: this.supplierSearch || "",
          itemsPerPage: 25,
        });
        const fetched = Array.isArray(data) ? data : data.data || [];
        this.supplierOptions = this.mergeDealSuppliersIntoOptions(fetched, dealSuppliers);
      } catch (e) {
        this.supplierOptions = dealSuppliers;
      }
    },
    mergeDealSuppliersIntoOptions(options, dealSuppliers) {
      let merged = options || [];
      (dealSuppliers || []).forEach((company) => {
        if (!merged.some((item) => Number(item.id) === Number(company.id))) {
          merged = [{ ...company, id: Number(company.id) }, ...merged];
        }
      });
      return merged;
    },
    openCreatePoFromSo(item) {
      this.createPoFromSoId = item.id;
      const suppliers = this.dealSupplierOptions;
      if (suppliers.length === 1) {
        this.createPoSupplierId = suppliers[0].id;
        this.confirmCreatePoFromSo();
        return;
      }
      this.createPoSupplierId = null;
      this.createPoDialog = true;
      this.loadSuppliers();
    },
    confirmCreatePoFromSo() {
      if (!this.createPoFromSoId || !this.createPoSupplierId) {
        return;
      }
      this.createPoDialog = false;
      this.$router.push({
        name: "stock-purchase-orders-create",
        query: this.dealQuery({
          sales_order_id: this.createPoFromSoId,
          supplier_company_id: this.createPoSupplierId,
        }),
      });
    },
    createDeliveryFromSo(item) {
      this.$router.push({
        name: "stock-sales-create",
        query: this.dealQuery({ sales_order_id: item.id }),
      });
    },
    organizeTransportFromSo(item) {
      this.$router.push({
        name: "stock-transport-orders-create",
        query: this.dealQuery({ sales_order_id: item.id, direction: "outbound" }),
      });
    },
    createReceiptFromPo(item) {
      this.$router.push({
        name: "stock-receipts-create",
        query: this.dealQuery({ purchase_order_id: item.id }),
      });
    },
    organizeTransportFromPo(item) {
      this.$router.push({
        name: "stock-transport-orders-create",
        query: this.dealQuery({ purchase_order_id: item.id, direction: "inbound" }),
      });
    },
    createReceiptFromTo(item) {
      this.$router.push({
        name: "stock-receipts-create",
        query: this.dealQuery({ transport_order_id: item.id }),
      });
    },
    createDeliveryFromTo(item) {
      this.$router.push({
        name: "stock-sales-create",
        query: this.dealQuery({ transport_order_id: item.id }),
      });
    },
    downloadNodeFile(node, file) {
      const action = DOWNLOAD_BY_KIND[node.kind];
      if (!action || !node?.id || !file?.id) {
        return;
      }

      this.downloadingFileId = file.id;
      this.$store
        .dispatch(action, {
          id: node.id,
          fileId: file.id,
          originalName: file.original_name,
          name: file.name,
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message:
              (error && error.message) ||
              this.$t("GENERAL.UNEXPECTED_ERROR"),
            color: "error",
          });
        })
        .finally(() => {
          this.downloadingFileId = null;
        });
    },
    async loadDeal() {
      this.loading = true;
      try {
        this.deal = await this.$store.dispatch(GET_DEAL, this.dealId);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.deal-quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.deal-workflow-standalone {
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  background: #fafbfc;
}
</style>
